import moment from 'moment';
import HeroImageUrl from 'assets/images/ferry_hero.jpg';
import Mykonos from 'assets/images/destinations/Europe/Greece/Mykonos.jpg';
import Santorini from 'assets/images/destinations/Europe/Greece/Santorini.jpg';
import Chania from 'assets/images/destinations/Europe/Greece/Chania.jpg';
import AnekLines from 'assets/images/ferryLogo/anek_lines_logo.svg';
import AegeanSpeedLines from 'assets/images/ferryLogo/aegean_speed_lines_logo.svg';
import GolderStarFerries from 'assets/images/ferryLogo/golden_star_ferries_logo.svg';
import MinoanLines from 'assets/images/ferryLogo/minoan_lines_logo.svg';
import Seajets from 'assets/images/ferryLogo/seajets_logo.svg';
import {replaceCmsUrl} from 'cms/helpers/common';
import {getPosts} from 'cms/blog/helpers/common';
import {formatPrice, formatDateShort} from 'lib/helpers/globalize-helper';
import {cms, isAffiliate, language, bookingBaseUrl} from 'helpers/stableValues';
import {
  ferryHomeTranslations,
  ferryHeroTranslations,
  ferryCompaniesTranslations,
  ferryDealsTranslations
} from 'ferries/helpers/translations';

const {readMore, viewAllReviewsOn, findTickets, bookNow, readLess} = ferryHomeTranslations;

const {title, body} = ferryHeroTranslations;
const {title: companiesLabel, link: companiesLink} = ferryCompaniesTranslations;
const {title: dealsLabel} = ferryDealsTranslations;
const defaultDestinations = [
  {
    image: Mykonos,
    post_title: 'Mykonos',
    country: 'Greece'
  }, {
    image: Santorini,
    post_title: 'Santorini',
    country: 'Greece'
  }, {
    image: Chania,
    post_title: 'Chania',
    country: 'Greece'
  }
];
const defaultCompanies = [
  {
    image: AnekLines,
    post_title: 'anek lines'
  }, {
    image: AegeanSpeedLines,
    post_title: 'aegean spped lines'
  }, {
    image: GolderStarFerries,
    post_title: 'golden star ferries'
  }, {
    image: MinoanLines,
    post_title: 'minoan lines'
  }, {
    image: Seajets,
    post_title: 'seajets'
  }
];

const announcement = cms?.featured?.top_news_text;

const hero = {
  title: isAffiliate?.header_title ?? (cms?.featured?.header_text ?? title),
  body: isAffiliate?.header_subtitle ?? (cms?.featured?.header_sub_title ?? body),
  img: cms?.featured?.header_image?.url ?? HeroImageUrl
};

const latestUpdates = cms?.featured?.latest_updates ? {
  title: cms?.featured?.latest_updates_title,
  subtitle: cms?.featured?.latest_updates?.post_title,
  image: cms?.featured?.latest_updates.image,
  summary: cms.featured?.latest_updates?.post_excerpt,
  link: replaceCmsUrl(cms.featured?.latest_updates?.permanlink),
  readMore: readMore
} : undefined;

const benefitsAndUpsells = cms?.featured?.benefits ? {
  title: cms?.featured?.benefits_title,
  subtitle: cms?.featured?.benefits_subtitle,
  description: cms?.featured?.benefits_text,
  benefits: cms?.featured?.benefits,
  upsells: cms?.featured?.upsells,
  trs: {bookNow}
} : undefined;

const companyPartners = {
  title: cms?.featured?.company_label ?? companiesLabel,
  subtitle: cms?.featured?.company_sub_header,
  description: cms?.featured?.companies_description,
  url: cms?.featured?.all_companies_url,
  companies: isAffiliate?.companies ?? cms?.featured?.companies?.map(({company}) => company) ?? defaultCompanies,
  trs: {companiesLink}
};

const destinations = {
  title: cms?.featured?.destination_label ?? dealsLabel,
  subtitle: cms?.featured?.destinations_subtitle,
  description: cms?.featured?.destination_description,
  destinationsUrl: cms?.featured?.all_destinations_url,
  items: isAffiliate?.destinations ??
    cms?.featured?.destinations?.map(({destination}) => destination) ?? defaultDestinations
};

const transformRoutes = (routes) =>
  Object.keys(routes).reduce((acc, code) => {
    const {departure_name, departure_abbr, arrival_name, arrival_abbr, companyLogo, fastestCompanyName,
      fastestVesselName, fastestDuration, price_from, route_url} = routes[code];
    acc.push({
      code,
      arr: arrival_name,
      dep: departure_name,
      book: `/${language}/${bookingBaseUrl}/results#search/dep/${departure_abbr}/arr/${arrival_abbr}/date/${moment().add(2, 'days').format('YYYY-MM-DD')}/passengers/1/vehicles/0`, // eslint-disable-line
      companyLogo,
      company: fastestCompanyName,
      vessel: fastestVesselName,
      duration: fastestDuration,
      price: formatPrice(price_from),
      url: replaceCmsUrl(route_url)
    });
    return acc;
  }, []);

const routes = cms?.featured?.routes ? {
  title: cms?.featured?.routes_label,
  subtitle: cms?.featured?.routes_subtitle,
  description: cms?.featured?.routes_text,
  items: transformRoutes(cms?.featured?.routes),
  trs: {findTickets}
} : undefined;

const testimonials = cms?.featured?.testimonials_label ? {
  title: cms?.featured?.testimonials_label,
  subtitle: cms?.featured?.testimonials_subtitle,
  description: cms?.featured?.testimonials_description,
  url: cms?.featured?.testimonials_url,
  items: Array.isArray(cms?.featured?.testimonials) ? cms.featured.testimonials.map(({
    testimonials_title, testimonials_text,
    testimonials_name, testimonials_date, testimonials_stars, testimonials_source
  }) => ({
    title: testimonials_title,
    text: testimonials_text,
    initials: testimonials_name.split(' ').reduce((acc, item) => item[0] ? `${acc}${item[0]}` : acc, ''),
    name: testimonials_name,
    date: testimonials_date && formatDateShort(moment(testimonials_date, 'DD/MM/YYYY')),
    stars: parseInt(testimonials_stars),
    source: testimonials_source
  })) : [],
  trs: {viewAllReviewsOn, readMore, readLess}
} : undefined;

const articles = (cms?.featured?.posts && cms.featured.posts.length > 0) ? {
  title: cms?.featured?.blog_title,
  permanlink: replaceCmsUrl(cms?.featured?.blog_url),
  viewMore: cms?.featured?.blog_url_label,
  posts: getPosts(cms?.featured?.posts)
} : undefined;

const manageBooking = cms?.featured?.manage_booking?.manage_booking_data ? 
  cms?.featured?.manage_booking : undefined;

export {
  announcement,
  hero,
  latestUpdates,
  benefitsAndUpsells,
  companyPartners,
  destinations,
  routes,
  testimonials,
  articles,
  manageBooking
};
